// src/pages/accountpage/FinanceQueue.jsx
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  financeQueue,
  financeApprove,
  financeReject,
  markPaid,
} from "../../api";
import "./finance-queue.css";

const FIN_USER = { id: 21 }; // ← replace with real auth
const INR = (n) => `₹${Number(n || 0).toFixed(2)}`;

const STATUS_CLASS = {
  MANAGER_APPROVED: "badge bg-soft-primary text-primary",
  UNDER_FINANCE_REVIEW: "badge bg-soft-warning text-warning",
  APPROVED_FOR_PAYMENT: "badge bg-soft-success text-success",
  PAID: "badge bg-soft-dark text-dark",
  REJECTED: "badge bg-soft-danger text-danger",
};
const StatusBadge = ({ s }) => (
  <span className={STATUS_CLASS[s] || "badge bg-soft-secondary text-secondary"}>
    {s?.replaceAll("_", " ")}
  </span>
);

export default function FinanceQueue() {
  // data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // tabs / sort / search
  const [tab, setTab] = useState("ALL"); // ALL | REVIEW | READY | PAID
  const [q, setQ] = useState("");
  const [orMode, setOrMode] = useState(true);
  const [sortKey, setSortKey] = useState("updated_at");
  const [sortDir, setSortDir] = useState("desc");

  // extra filters
  const ALL_STATUSES = [
    "MANAGER_APPROVED",
    "UNDER_FINANCE_REVIEW",
    "APPROVED_FOR_PAYMENT",
    "PAID",
  ];
  const [fltStatuses, setFltStatuses] = useState(new Set(ALL_STATUSES));
  const [amtMin, setAmtMin] = useState("");
  const [amtMax, setAmtMax] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // status dropdown ui
  const [statusOpen, setStatusOpen] = useState(false);
  const toggleStatusMenu = () => setStatusOpen((v) => !v);
  const closeStatusMenu = () => setStatusOpen(false);

  // selection
  const [selected, setSelected] = useState(new Set());

  // mark-paid modal
  const [payOpen, setPayOpen] = useState(false);
  const [payRow, setPayRow] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMode, setPayMode] = useState("NEFT");
  const [payRef, setPayRef] = useState("");
  const [payRemarks, setPayRemarks] = useState("");

  // reject modal
  const [rejOpen, setRejOpen] = useState(false);
  const [rejRow, setRejRow] = useState(null);
  const [rejReason, setRejReason] = useState("");

  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      setLoading(true);
      setErr("");
      const { data } = await financeQueue({
        tab,
        q,
        sort: sortKey,
        dir: sortDir,
        page: 1,
        page_size: 200,
      });
      setRows((data && data.items) || []);
    } catch (e) {
      setErr(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, sortKey, sortDir]);

  // derived list (client filters)
  const filtered = useMemo(() => {
    let list = rows.slice();

    if (tab === "REVIEW") {
      list = list.filter((r) =>
        ["MANAGER_APPROVED", "UNDER_FINANCE_REVIEW"].includes(r.status_code)
      );
    } else if (tab === "READY") {
      list = list.filter((r) => r.status_code === "APPROVED_FOR_PAYMENT");
    } else if (tab === "PAID") {
      list = list.filter((r) => r.status_code === "PAID");
    }

    list = list.filter((r) => fltStatuses.has(r.status_code));

    const needle = q.trim().toLowerCase();
    if (needle) {
      const tokens = needle.split(/\s+/).filter(Boolean);
      list = list.filter((r) => {
        const hay = `#${r.id} ${r.title || ""} ${r.requester || ""} ${r.status_code || ""}`.toLowerCase();
        return orMode ? tokens.some((t) => hay.includes(t)) : tokens.every((t) => hay.includes(t));
      });
    }

    const min = amtMin === "" ? -Infinity : Number(amtMin);
    const max = amtMax === "" ? +Infinity : Number(amtMax);
    list = list.filter((r) => {
      const a = Number(r.amount || 0);
      return a >= min && a <= max;
    });

    const fromTs = dateFrom ? new Date(dateFrom).getTime() : -Infinity;
    const toTs = dateTo ? new Date(dateTo + "T23:59:59").getTime() : +Infinity;
    list = list.filter((r) => {
      const t = r.updated_at ? new Date(r.updated_at).getTime() : 0;
      return t >= fromTs && t <= toTs;
    });

    list.sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      if (sortKey === "amount") {
        av = Number(a.amount || 0);
        bv = Number(b.amount || 0);
      }
      const cmp = av > bv ? 1 : av < bv ? -1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });

    return list;
  }, [rows, tab, fltStatuses, q, orMode, amtMin, amtMax, dateFrom, dateTo, sortKey, sortDir]);

  const totals = useMemo(
    () => ({
      count: filtered.length,
      amount: filtered.reduce((s, r) => s + Number(r.amount || 0), 0),
    }),
    [filtered]
  );

  // tiny icon button
  const IconBtn = ({ className = "", title, onClick, disabled, children }) => (
    <button className={`btn btn-xxs btn-icon ${className}`} title={title} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );

  // selection
  function toggleAll(e) {
    if (e.target.checked) setSelected(new Set(filtered.map((r) => r.id)));
    else setSelected(new Set());
  }
  function toggleOne(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  // actions
  async function approveOne(id) {
    setBusy(true);
    try {
      await financeApprove(id, { actor_id: FIN_USER.id });
      await load();
    } catch (e) {
      alert(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  }
  async function approveSelected() {
    if (!selected.size) return;
    if (!window.confirm(`Approve ${selected.size} reports for payment?`)) return;
    setBusy(true);
    try {
      for (const id of selected) await financeApprove(id, { actor_id: FIN_USER.id });
      setSelected(new Set());
      await load();
    } catch (e) {
      alert(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  }

  function openPay(row) {
    setPayRow(row);
    setPayAmount(String(Number(row.amount || 0)));
    setPayMode("NEFT");
    setPayRef("");
    setPayRemarks("");
    setPayOpen(true);
  }
  async function doPay() {
    const amt = Number(payAmount || 0);
    if (!amt || amt <= 0) return alert("Enter a valid amount.");
    setBusy(true);
    try {
      await markPaid(payRow.id, {
        actor_id: FIN_USER.id,
        approved_amount: amt,
        payment_mode: payMode,
        reference_no: payRef || null,
        remarks: payRemarks || null,
      });
      setPayOpen(false);
      await load();
    } catch (e) {
      alert(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  }

  function openReject(row) {
    setRejRow(row);
    setRejReason("");
    setRejOpen(true);
  }
  async function doReject() {
    if (!rejReason.trim()) return alert("Please provide a reason.");
    setBusy(true);
    try {
      await financeReject(rejRow.id, { actor_id: FIN_USER.id, comment: rejReason.trim() });
      setRejOpen(false);
      await load();
    } catch (e) {
      alert(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  }

  // export
  function exportCSV() {
    const header = ["ID", "Title", "Requester", "Status", "Amount", "Updated At"];
    const body = filtered.map((r) => [
      r.id,
      (r.title || "").replace(/"/g, '""'),
      (r.requester || "").replace(/"/g, '""'),
      r.status_code,
      Number(r.amount || 0),
      r.updated_at || "",
    ]);
    const csv =
      header.join(",") +
      "\n" +
      body.map((row) => row.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "finance-queue.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  }

  // counts (optional, if you want badges later)
  const counts = useMemo(() => {
    const c = { ALL: rows.length, REVIEW: 0, READY: 0, PAID: 0 };
    rows.forEach((r) => {
      if (["MANAGER_APPROVED", "UNDER_FINANCE_REVIEW"].includes(r.status_code)) c.REVIEW++;
      if (r.status_code === "APPROVED_FOR_PAYMENT") c.READY++;
      if (r.status_code === "PAID") c.PAID++;
    });
    return c;
  }, [rows]);

  return (
    <div className="finance-queue container-xxl py-3">
      {/* Header / toolbar */}
      <div className="card card-flat">
        <div className="card-body">
          <div className="fq-grid">
            {/* Tabs */}
            <div className="fq-tabs">
              {["ALL", "REVIEW", "READY", "PAID"].map((t) => (
                <button
                  key={t}
                  className={`btn btn-xs ${tab === t ? "btn-primary" : "btn-outline-secondary"} rounded-pill`}
                  onClick={() => setTab(t)}
                >
                  {t === "ALL" ? "All" : t === "REVIEW" ? "In review" : t === "READY" ? "Ready to pay" : "Paid"}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="fq-search input-group input-group-xxs">
              <span className="input-group-text">Search</span>
              <input
                className="form-control"
                placeholder="title, requester, #id…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <button
                className={`btn ${orMode ? "btn-soft-success" : "btn-soft-secondary"} rounded-end`}
                title={orMode ? "OR search" : "AND search"}
                onClick={() => setOrMode((v) => !v)}
              >
                {orMode ? "OR" : "AND"}
              </button>
            </div>

            {/* Sort */}
            <div className="fq-sort input-group input-group-xxs">
              <span className="input-group-text">Sort</span>
              <select className="form-select" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
                <option value="updated_at">Updated</option>
                <option value="amount">Amount</option>
                <option value="title">Title</option>
                <option value="id">ID</option>
              </select>
              <button
                className="btn btn-soft-secondary"
                onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                title="Toggle sort direction"
              >
                {sortDir === "asc" ? "↑" : "↓"}
              </button>
            </div>

            {/* Status (dropdown) */}
            <div className="fq-status">
              <div className="dropdown-wrap" onBlur={closeStatusMenu} tabIndex={0}>
                <button
                  type="button"
                  className="btn btn-xs btn-soft rounded-pill w-100 text-start d-inline-flex align-items-center justify-content-between"
                  onClick={toggleStatusMenu}
                >
                  <span>Status{fltStatuses.size !== ALL_STATUSES.length ? ` (${fltStatuses.size})` : ""}</span>
                  <span className="ms-2">▾</span>
                </button>

                {statusOpen && (
                  <div className="dropdown-pane">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong className="tiny">Filter status</strong>
                      <div className="d-flex gap-1">
                        <button className="btn btn-xxs btn-soft" type="button"
                          onClick={() => setFltStatuses(new Set(ALL_STATUSES))}>All</button>
                        <button className="btn btn-xxs btn-soft" type="button"
                          onClick={() => setFltStatuses(new Set())}>None</button>
                      </div>
                    </div>
                    <div className="status-list">
                      {ALL_STATUSES.map((s) => (
                        <label key={s} className="form-check d-flex align-items-center gap-2 tiny">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={fltStatuses.has(s)}
                            onChange={() =>
                              setFltStatuses((prev) => {
                                const n = new Set(prev);
                                n.has(s) ? n.delete(s) : n.add(s);
                                return n;
                              })
                            }
                          />
                          <span>{s.replaceAll("_", " ")}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Amount */}
            <div className="fq-amount input-group input-group-xxs">
              <span className="input-group-text">Amount</span>
              <input
                type="number"
                className="form-control"
                placeholder="min"
                value={amtMin}
                onChange={(e) => setAmtMin(e.target.value)}
              />
              <input
                type="number"
                className="form-control"
                placeholder="max"
                value={amtMax}
                onChange={(e) => setAmtMax(e.target.value)}
              />
            </div>

            {/* Date */}
            <div className="fq-date input-group input-group-xxs">
              <span className="input-group-text">Date</span>
              <input type="date" className="form-control" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              <input type="date" className="form-control" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            </div>

            {/* Export */}
            <div className="fq-export text-end">
              <button className="btn btn-xs btn-soft rounded-pill" onClick={exportCSV}>⤓ Export</button>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk actions bar */}
      {selected.size > 0 && (
        <div className="alert alert-soft-info py-2 d-flex align-items-center justify-content-between rounded-3 shadow-sm">
          <div><b>{selected.size}</b> selected</div>
          <div className="d-flex gap-2">
            <button className="btn btn-xxs btn-soft-success rounded-pill" disabled={busy} onClick={approveSelected}>
              {busy ? "Approving…" : "Approve selected"}
            </button>
            <button className="btn btn-xxs btn-soft rounded-pill" onClick={() => setSelected(new Set())}>
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card shadow-sm">
        <div className="card-body p-0">
          {err && <div className="alert alert-danger m-3">{err}</div>}
          {loading ? (
            <div className="p-4 text-center text-muted">Loading…</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-compact align-middle mb-0">
                <thead className="table-sticky">
                  <tr>
                    <th style={{ width: 30 }}>
                      <input
                        type="checkbox"
                        className="form-check-input rounded-2"
                        checked={selected.size && selected.size === filtered.length}
                        onChange={toggleAll}
                      />
                    </th>
                    <th style={{ width: 70 }} className="text-muted">#</th>
                    <th>Title / Requester</th>
                    <th style={{ width: 180 }}>Status</th>
                    <th style={{ width: 140 }} className="text-end">Amount</th>
                    <th style={{ width: 180 }}>Updated</th>
                    <th style={{ width: 220 }} className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input rounded-2"
                          checked={selected.has(r.id)}
                          onChange={() => toggleOne(r.id)}
                        />
                      </td>
                      <td className="text-muted">#{r.id}</td>
                      <td>
                        <div className="fw-semibold line-clamp-1">
                          <Link className="link-quiet" to={`/admin/accounts/reports/${r.id}`}>{r.title}</Link>
                        </div>
                        <div className="text-muted tiny">by {r.requester || "-"}</div>
                      </td>
                      <td><StatusBadge s={r.status_code} /></td>
                      <td className="text-end fw-semibold">{INR(r.amount)}</td>
                      <td className="text-muted tiny">
                        {r.updated_at ? new Date(r.updated_at).toLocaleString() : "-"}
                      </td>
                      <td className="text-end">
                        <div className="d-inline-flex gap-1">
                          <IconBtn
                            className="btn-soft-success"
                            disabled={busy || r.status_code === "APPROVED_FOR_PAYMENT"}
                            onClick={() => approveOne(r.id)}
                            title="Approve for payment"
                          >
                            ✓
                          </IconBtn>
                          <IconBtn
                            className="btn-soft-danger"
                            disabled={busy || r.status_code === "PAID"}
                            onClick={() => openReject(r)}
                            title="Reject"
                          >
                            ✕
                          </IconBtn>
                          <IconBtn
                            className="btn-soft-primary"
                            disabled={busy || r.status_code === "PAID"}
                            onClick={() => openPay(r)}
                            title="Mark as Paid"
                          >
                            ₹
                          </IconBtn>
                          <Link className="btn btn-xxs btn-soft rounded-pill" title="View details"
                                to={`/admin/accounts/reports/${r.id}`}>
                            View
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!filtered.length && (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        No items found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* MARK PAID MODAL */}
      {payOpen && (
        <div className="modal fade show" style={{ display: "block" }} tabIndex="-1"
             onClick={() => !busy && setPayOpen(false)}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Mark Paid — #{payRow?.id} • {payRow?.title}</h6>
                <button type="button" className="btn-close" onClick={() => !busy && setPayOpen(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-2 text-muted tiny">Report total: <b>{INR(payRow?.amount)}</b></div>
                <div className="row g-2">
                  <div className="col-md-4">
                    <label className="form-label tiny">Amount</label>
                    <input className="form-control form-control-sm" type="number" step="0.01"
                           value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label tiny">Mode</label>
                    <select className="form-select form-select-sm" value={payMode}
                            onChange={(e) => setPayMode(e.target.value)}>
                      <option>NEFT</option><option>IMPS</option><option>UPI</option>
                      <option>CASH</option><option>CHEQUE</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label tiny">Reference</label>
                    <input className="form-control form-control-sm" value={payRef}
                           onChange={(e) => setPayRef(e.target.value)} placeholder="Txn / Cheque / UTR" />
                  </div>
                  <div className="col-12">
                    <label className="form-label tiny">Remarks (optional)</label>
                    <input className="form-control form-control-sm" value={payRemarks}
                           onChange={(e) => setPayRemarks(e.target.value)} placeholder="Notes for history / email" />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-xxs btn-soft rounded-pill" disabled={busy}
                        onClick={() => setPayOpen(false)}>Close</button>
                <button className="btn btn-xxs btn-primary rounded-pill" disabled={busy} onClick={doPay}>
                  {busy ? "Saving…" : "Confirm Paid"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* REJECT MODAL */}
      {rejOpen && (
        <div className="modal fade show" style={{ display: "block" }} tabIndex="-1"
             onClick={() => !busy && setRejOpen(false)}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Reject — #{rejRow?.id} • {rejRow?.title}</h6>
                <button type="button" className="btn-close" onClick={() => !busy && setRejOpen(false)} />
              </div>
              <div className="modal-body">
                <label className="form-label tiny">Reason</label>
                <textarea className="form-control" rows={3} value={rejReason}
                          onChange={(e) => setRejReason(e.target.value)} placeholder="Why is this being rejected?" />
              </div>
              <div className="modal-footer">
                <button className="btn btn-xxs btn-soft rounded-pill" disabled={busy}
                        onClick={() => setRejOpen(false)}>Close</button>
                <button className="btn btn-xxs btn-danger rounded-pill" disabled={busy} onClick={doReject}>
                  {busy ? "Saving…" : "Confirm Reject"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
